<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import { useCurrencyFormaterFromCents } from '@/composable/useCurrencyFormater'
import { useFareOptimizerStore } from '@/store/fareOptimizer'

const { t } = useI18n()
const store = useFareOptimizerStore()

// As recomendações de ação refletem exatamente as oportunidades curadas do
// relatório (store.reportOpportunities) — incluir/remover/editar no editor altera
// esta lista na hora, e é a MESMA informação (descrição + ação) que vai para o PDF.
const { reportOpportunities } = storeToRefs(store)

const openIndexes = ref<number[]>([])

function toggleAccordion(index: number) {
  openIndexes.value = openIndexes.value.includes(index)
    ? openIndexes.value.filter((i) => i !== index)
    : [...openIndexes.value, index]
}

const recommendations = computed(() =>
  reportOpportunities.value.map((opp, index) => ({
    index,
    title: opp.titulo || opp.categoria || `${t('contract_optimizer.opportunities.new_category')} ${index + 1}`,
    category: opp.categoria,
    descricao: opp.descricao,
    acao: opp.acao,
    economiaMensal: opp.economia_mensal,
    economiaAnual: opp.economia_anual,
    open: openIndexes.value.includes(index),
  })),
)
</script>

<template>
  <div class="bg-white shadow-4">
    <div class="flex items-center justify-between border-b-2 border-stroke px-6 py-4">
      <h1 class="border-stroke font-satoshi text-base font-bold text-black">Recomendação de ação</h1>
    </div>
    <div class="p-4 sm:p-6 xl:p-6">
      <p v-if="recommendations.length === 0" class="py-6 text-center text-sm text-body">
        {{ t('contract_optimizer.opportunities.empty') }}
      </p>

      <div v-else class="flex flex-col gap-4">
        <div v-for="item in recommendations" :key="item.index" class="border border-stroke p-4 shadow-9 md:p-6 xl:p-4">
          <button @click="toggleAccordion(item.index)" class="flex w-full items-center justify-between gap-2">
            <div class="text-left">
              <p v-if="item.category" class="text-xs font-semibold uppercase tracking-wide text-slate-400">{{ item.category }}</p>
              <h1 class="border-stroke font-satoshi text-base font-bold text-black">{{ item.title }}</h1>
            </div>

            <div class="flex items-center gap-3">
              <span class="whitespace-nowrap font-satoshi text-sm font-bold text-primary">
                {{ useCurrencyFormaterFromCents(item.economiaMensal) }}<span class="text-xs font-medium text-slate-400">/mês</span>
              </span>
              <div class="flex h-9 w-9 items-center justify-center border border-primary dark:border-white">
                <svg
                  v-if="!item.open"
                  class="fill-primary dark:fill-white"
                  width="15"
                  height="15"
                  viewBox="0 0 15 15"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M13.2969 6.51563H8.48438V1.70312C8.48438 1.15625 8.04688 0.773438 7.5 0.773438C6.95313 0.773438 6.57031 1.21094 6.57031 1.75781V6.57031H1.75781C1.21094 6.57031 0.828125 7.00781 0.828125 7.55469C0.828125 8.10156 1.26563 8.48438 1.8125 8.48438H6.625V13.2969C6.625 13.8438 7.0625 14.2266 7.60938 14.2266C8.15625 14.2266 8.53906 13.7891 8.53906 13.2422V8.42969H13.3516C13.8984 8.42969 14.2813 7.99219 14.2813 7.44531C14.2266 6.95312 13.7891 6.51563 13.2969 6.51563Z"
                    fill="" />
                </svg>
                <svg
                  v-else
                  class="fill-primary dark:fill-white"
                  width="15"
                  height="3"
                  viewBox="0 0 15 3"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M13.503 0.447144C13.446 0.447144 13.503 0.447144 13.503 0.447144H1.49482C0.925718 0.447144 0.527344 0.902427 0.527344 1.47153C0.527344 2.04064 0.982629 2.43901 1.55173 2.43901H13.5599C14.129 2.43901 14.5273 1.98373 14.5273 1.41462C14.4704 0.902427 14.0151 0.447144 13.503 0.447144Z"
                    fill="" />
                </svg>
              </div>
            </div>
          </button>

          <div v-show="item.open" class="mt-5 flex flex-col gap-3 duration-200 ease-in-out">
            <p v-if="item.descricao" class="max-w-[830px] font-medium text-body">{{ item.descricao }}</p>
            <p v-if="item.acao" class="max-w-[830px] border-l-2 border-primary pl-3 font-medium text-black">
              <b>{{ t('contract_optimizer.opportunities.action') }}:</b> {{ item.acao }}
            </p>
            <p class="text-sm text-slate-500">
              {{ t('contract_optimizer.opportunities.yearly') }}: <b>{{ useCurrencyFormaterFromCents(item.economiaAnual) }}</b>
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
